import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import * as React from 'react';
/** Load Google Maps API. */
export function useGoogleMaps(params) {
    const [googleApi, setGoogleApi] = React.useState();
    React.useEffect(() => {
        doAsync();
        async function doAsync() {
            const loader = new Loader(params.apiOptions);
            setGoogleApi(await loader.load());
        }
    }, []);
    return googleApi;
}
const DEFAULT_MAP_OPTIONS = {
    center: { lat: 0, lng: 0 },
    zoom: 3,
};
/**
 * Create new map instance, this does not automatically update when parameters change.
 * @param params
 * @returns
 */
export function useMap(params) {
    const refMapElement = React.useRef(null);
    const [map, setMap] = React.useState();
    React.useEffect(() => {
        if (!params.api) {
            return;
        }
        if (!refMapElement.current) {
            return;
        }
        if (map) {
            return;
        }
        setMap(new params.api.maps.Map(refMapElement.current, {
            ...DEFAULT_MAP_OPTIONS,
            ...(params.mapOptions ?? {}),
        }));
    }, [params.api]);
    return [refMapElement, map];
}
export function useMarkers(params) {
    const [bounds, setBounds] = React.useState();
    const [clusterer, setClusterer] = React.useState();
    const [markers, setMarkers] = React.useState([]);
    React.useEffect(() => {
        if (!params.api) {
            return;
        }
        if (!params.map) {
            return;
        }
        // Remove clusterer.
        if (clusterer) {
            clusterer.clearMarkers();
            clusterer.setMap(null);
        }
        // Remove existing markers from map.
        markers.forEach((marker) => {
            marker.setMap(null);
        });
        // Create new markers array.
        const newMarkers = [];
        // Create bounds instance to track marker bounds.
        const newBounds = new params.api.maps.LatLngBounds();
        // Create new markers.
        params.markers.forEach((marker) => {
            if (params.api && marker.position) {
                let icon = undefined;
                if (marker.icon) {
                    icon = {
                        anchor: new google.maps.Point(marker.icon.anchor.x, marker.icon.anchor.y),
                        scaledSize: new google.maps.Size(marker.icon.scaledSize.width, marker.icon.scaledSize.height),
                        size: new google.maps.Size(marker.icon.size.width, marker.icon.size.height),
                        url: marker.icon.url,
                    };
                }
                else if (params.icon) {
                    icon = {
                        anchor: new google.maps.Point(params.icon.anchor.x, params.icon.anchor.y),
                        scaledSize: new google.maps.Size(params.icon.scaledSize.width, params.icon.scaledSize.height),
                        size: new google.maps.Size(params.icon.size.width, params.icon.size.height),
                        url: params.icon.url,
                    };
                }
                const instance = new params.api.maps.Marker({
                    ...marker,
                    icon: icon,
                    map: params.map,
                });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                instance.addListener('click', (event) => {
                    event.domEvent.stopPropagation();
                    if (params.onMarkerClick) {
                        params.onMarkerClick(instance, marker);
                        // Move map to center point and zoom.
                        if (params.map && marker.position) {
                            params.map.setCenter(marker.position);
                            params.map.setZoom(16);
                        }
                    }
                });
                newMarkers.push(instance);
                newBounds.extend(marker.position);
            }
        });
        setMarkers(newMarkers);
        setBounds(newBounds);
        if (params.clustering) {
            const newClusterer = new MarkerClusterer({
                map: params.map,
                markers: newMarkers,
            });
            setClusterer(newClusterer);
        }
    }, [params.api, params.map, JSON.stringify(params.markers)]);
    return [markers, bounds];
}
export function useCircle(params) {
    const [circle, setCircle] = React.useState();
    React.useEffect(() => {
        if (!params.api) {
            return;
        }
        if (!params.map) {
            return;
        }
        if (params.clear) {
            // Clear existing circle.
            if (circle) {
                circle.setMap(null);
            }
            return;
        }
        if (!params.center) {
            return;
        }
        if (!params.radius) {
            return;
        }
        // Clear existing circle.
        if (circle) {
            circle.setMap(null);
        }
        // Add new circle.
        const newCircle = new params.api.maps.Circle({
            center: params.center,
            map: params.map,
            radius: params.radius,
        });
        setCircle(newCircle);
    }, [params.api, params.center?.lat, params.center?.lng, params.radius, params.map]);
    return [circle];
}
